/**
 * Analysis intent → AnalysisSpec engine (M8-B, acceptance #9). One declarative
 * `AnalysisIntent` is the structured request BOTH input modes emit — the UI
 * action and an M5 NL prompt ("build 5 balanced territories", "show 20-minute
 * drive time"). `buildAnalysisFromIntent` is the deterministic ENGINE that folds
 * an intent into the `analysis` block, calling the exact same per-tool builders
 * the panel calls. Because both modes funnel through this one function, an
 * NL-emitted intent produces the SAME `analysis` block as the equivalent UI
 * action by construction (the LLM only plans; the engine builds).
 */

import type { PointSet } from "@/lib/map/pointset";
import {
  createStubIsochroneProvider,
  haversineRoutingProvider,
  type IsochroneBand,
  type RoutingProvider,
} from "@/lib/geo/routing";
import { buildAutomatedTerritories } from "./territories";
import { buildRadiusCircle } from "./radius";
import { buildDriveTime, type DriveTimeOriginRefs } from "./driveTime";
import { buildRouteOptimization } from "./routes";
import { emptyAnalysis, type AnalysisSpec, type RadiusCenter } from "./spec";

/** The structured analysis request — the shared "plan" the engine executes. */
export type AnalysisIntent =
  | { kind: "territories"; count: number; balanceBy?: string }
  | { kind: "radius"; center: RadiusCenter; radiusMeters: number; label?: string; id?: string }
  | { kind: "driveTime"; originRefs: DriveTimeOriginRefs; bands: IsochroneBand[] }
  | { kind: "routes"; stopRowIds: string[] };

export interface AnalysisIntentDeps {
  /** Token palette to color territories / drive-time bands. */
  palette: string[];
  /** Routing provider for route optimization (OSS haversine default). */
  routing?: RoutingProvider;
  /** Isochrone provider for drive-time (labeled stub default). */
  isochrone?: RoutingProvider;
}

/**
 * Fold an `AnalysisIntent` into a base `analysis` block, returning the next
 * block. Async because drive-time / routes call the (injected) provider. A
 * graceful route refusal (too few / too many stops) leaves `routes` untouched;
 * the detailed refusal UX belongs to the caller via `buildRouteOptimization`.
 */
export async function buildAnalysisFromIntent(
  base: AnalysisSpec | undefined,
  intent: AnalysisIntent,
  pointSet: PointSet,
  deps: AnalysisIntentDeps,
): Promise<AnalysisSpec> {
  const a: AnalysisSpec = base ? { ...base } : emptyAnalysis();

  switch (intent.kind) {
    case "territories": {
      const items = buildAutomatedTerritories(pointSet, {
        count: Math.max(1, intent.count),
        balanceBy: intent.balanceBy,
        palette: deps.palette,
      });
      return { ...a, territories: { mode: "automated", items, balanceBy: intent.balanceBy } };
    }
    case "radius": {
      const circle = buildRadiusCircle(pointSet, intent.center, intent.radiusMeters, {
        id: intent.id,
        label: intent.label,
      });
      return { ...a, radii: [...(a.radii ?? []), circle] };
    }
    case "driveTime": {
      const provider = deps.isochrone ?? createStubIsochroneProvider();
      const { driveTime } = await buildDriveTime(pointSet, intent.originRefs, intent.bands, provider, {
        palette: deps.palette,
      });
      return { ...a, driveTime };
    }
    case "routes": {
      const provider = deps.routing ?? haversineRoutingProvider();
      const res = await buildRouteOptimization(pointSet, intent.stopRowIds, provider);
      return res.ok ? { ...a, routes: res.routes } : a;
    }
  }
}
